import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getLocales } from 'expo-localization';
import { I18nManager, Platform } from 'react-native';
import { claimNativeRtlReloadSlot, reloadNativeApp } from './reload-native';
import {
  getPersistedLocaleAsync,
  getPersistedLocaleSync,
  type SupportedLocale,
} from '../lib/app-preferences-storage';

// English translations
import enCommon from './locales/en/common';
import enAuth from './locales/en/auth';
import enSeeker from './locales/en/seeker';
import enEmployer from './locales/en/employer';
import enTour from './locales/en/tour';

// Arabic translations
import arCommon from './locales/ar/common';
import arAuth from './locales/ar/auth';
import arSeeker from './locales/ar/seeker';
import arEmployer from './locales/ar/employer';
import arTour from './locales/ar/tour';

function getDeviceLocale(): SupportedLocale {
  const languageCode = getLocales()[0]?.languageCode;
  return languageCode === 'ar' ? 'ar' : 'en';
}

async function readNativePersistedLocale(): Promise<SupportedLocale | null> {
  if (Platform.OS === 'web') return null;
  return getPersistedLocaleAsync();
}

function getInitialLocale(): SupportedLocale {
  return getPersistedLocaleSync() ?? getDeviceLocale();
}

const initialLocale = getInitialLocale();

// Reconcile I18nManager direction with the chosen locale on native. The native
// I18nManager flag is persisted across launches on both Android and iOS, but
// the JS-side language was not (until now): so a previously-Arabic user could
// relaunch into LTR text or vice versa. Force the correct direction up front;
// if it changed we trigger a one-time reload so the native views pick it up.
function reconcileNativeDirection(locale: SupportedLocale) {
  if (Platform.OS === 'web') return;
  const shouldBeRTL = locale === 'ar';
  if (I18nManager.isRTL !== shouldBeRTL) {
    I18nManager.allowRTL(shouldBeRTL);
    I18nManager.forceRTL(shouldBeRTL);
    void claimNativeRtlReloadSlot().then((claimed) => {
      if (!claimed) return;
      // Defer to next tick so the import graph finishes before the reload.
      setTimeout(() => {
        void reloadNativeApp();
      }, 0);
    });
  }
}

async function reconcileNativePersistedLocale() {
  const persistedLocale = await readNativePersistedLocale();
  if (!persistedLocale) return;

  if (i18n.language !== persistedLocale) {
    await i18n.changeLanguage(persistedLocale);
  }
  reconcileNativeDirection(persistedLocale);
}

reconcileNativeDirection(initialLocale);

void i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        seeker: enSeeker,
        employer: enEmployer,
        tour: enTour,
      },
      ar: {
        common: arCommon,
        auth: arAuth,
        seeker: arSeeker,
        employer: arEmployer,
        tour: arTour,
      },
    },
    lng: initialLocale,
    fallbackLng: 'en',
    ns: ['common', 'auth', 'seeker', 'employer', 'tour'],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false, // React already handles escaping
    },
    compatibilityJSON: 'v4',
    react: {
      useSuspense: false, // Avoid suspense issues on native
    },
  })
  .then(() => {
    if (Platform.OS !== 'web') {
      void reconcileNativePersistedLocale();
    }
  });

export default i18n;
